import { setRuntimeConfig } from 'lib/runtimeConfig';
import { configureStore } from 'domain/store';
import tokenKeeper from 'lib/apiTokenKeeper';
import router from 'domain/router/router';
import rootSaga from 'sagas/index';
import { Auth } from 'aws-amplify';
import { CognitoAuthError, ConfigParseError } from 'lib/customErrors';
import * as Sentry from '@sentry/browser';
import { requestEnvConfig } from 'initializers/envConfigRequest';

const DEFAULT_ENV = process.env.REACT_APP_ENV === 'local' ? process.env : {};

const ENV_PREFIX = 'REACT_APP_';

// Deliberately NOT part of the retried request, for two reasons: this failure
// path boots the store and root saga so the logout saga can run, which must not
// repeat per attempt; and it throws CognitoAuthError, which src/index.js checks
// with `instanceof` — routing it through Effect would surface the failure as a
// Cause instead of the raw error, breaking that check and wrongly rendering the
// error page instead of letting the logout saga run.
const getJwtToken = async () => {
  try {
    const session = await Auth.currentSession();

    return session.getAccessToken().getJwtToken();
  } catch {
    // this must be run to allow saga to logout user respecting backurl and other existing logout logic
    const store = configureStore(router.navigate);
    tokenKeeper.injectStore(store);
    store.runSaga(rootSaga, store.dispatch);

    throw new CognitoAuthError('User is not authorized or token retieval failed');
  }
};

async function envConfigInitializer() {
  const jwtToken = await getJwtToken();

  // Retries transient failures internally and throws ConfigFetchError once the
  // policy is exhausted; see initializers/envConfigRequest.
  const response = await requestEnvConfig(jwtToken);

  try {
    const { whitelabel = {}, configuration = {} } = await response.json();
    const env = Object.entries(configuration).reduce((acc, [k, v]) => {
      const key = `${ENV_PREFIX}${k}`;

      acc[key] = DEFAULT_ENV[key] || v;

      return acc;
    }, {});

    const wl = Object.entries(whitelabel).reduce((acc, [k, v]) => {
      acc[k] = v === '0' || v === '1' ? Boolean(Number(v)) : v;

      return acc;
    }, {});

    setRuntimeConfig('env', env);
    setRuntimeConfig('whitelabel', wl);
  } catch (e) {
    console.error('Failed to parse fetched env config');
    Sentry.captureMessage('Server config parse failed', {
      level: 'warning',
      tags: { source: 'SERVER_CONFIG_PARSE_FAILED' },
      extra: {
        cause: e,
      },
    });
    // throwing error to catch it in the main flow and render SWW page in in case error is instance of ConfigFetchError
    throw new ConfigParseError('Failed to parse env config file');
  }
}

export default envConfigInitializer;
